import { RouterView, type RouterOptions } from "vue-router";
import EntityAudits from "@/components/EntityAudits.vue";
import { EntityDocuments } from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
// Troubleshoot your routes using this - https://paths.esm.dev

const names = {
  permitVersions: "PermitVersion.PermitVersions",
  list: "PermitVersion.PermitVersionList",
  new: "PermitVersion.PermitVersionNew",
  base: "PermitVersion.PermitVersionBase",
  view: "PermitVersion.PermitVersionView",
  edit: "PermitVersion.PermitVersionEdit",
  issue: "PermitVersion.PermitVersionIssue",
  terminate: "PermitVersion.PermitVersionTerminate",
  notes: "PermitVersion.PermitVersionNotes",
  noteEdit: "PermitVersion.PermitVersionNoteEdit",
  noteNew: "PermitVersion.PermitVersionNoteNew",
  documents: "PermitVersion.PermitVersionDocuments",
  documentEdit: "PermitVersion.PermitVersionDocumentEdit",
  documentNew: "PermitVersion.PermitVersionDocumentNew",
  materialsPermitted: "PermitVersion.PermitVersionMaterialsPermitted",
  materialsPermittedEdit: "PermitVersion.PermitVersionMaterialsPermittedEdit",
  reportSchedules: "PermitVersion.PermitVersionReportSchedules",
  reportScheduleNew: "PermitVersion.PermitVersionReportScheduleNew",
  reportScheduleImport: "PermitVersion.PermitVersionReportScheduleImport",
  reportScheduleEdit: "PermitVersion.PermitVersionReportScheduleEdit",
  audit: "PermitVersion.PermitVersionAudit",
};

export { names as permitVersionRouteNames };

const permitVersionRoutes: RouterOptions["routes"] = [
  {
    path: "/permitVersions",
    name: names.permitVersions,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: { title: "Permit Versions", lookupGroups: ["PermitType", "PermitVersionType", "PermitVersionStatus"] },
        component: () => import("@/views/permitVersion/PermitVersionList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/permitVersion/PermitVersionBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "New PermitVersion", isNew: true },
            component: () => import("@/views/permitVersion/PermitVersionEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/permitVersion/PermitVersionBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Permit Version View" },
            component: () => import("@/views/permitVersion/PermitVersionView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Permit Version Edit", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionEdit.vue"),
          },
          {
            path: "issue",
            name: names.issue,
            meta: { title: "Issue Permit Version", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionIssue.vue"),
          },
          {
            path: "terminate",
            name: names.terminate,
            meta: { title: "Terminate Permit", isForm: true },
            component: () => import("@/views/permitVersion/PermitTerminate.vue"),
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Permit Version Internal Notes" },
            component: {
              render: () => <EntityNotes idField="permitVersionId" routeNamePrefix="PermitVersion.PermitVersionNote" />,
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Permit Version Internal Note", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Permit Version Internal Note", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionNote.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Permit Version Documents" },
            component: {
              render: () => (
                <EntityDocuments idField="permitVersionId" routeNamePrefix="PermitVersion.PermitVersionDocument" />
              ),
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentEdit,
            meta: { title: "Edit Permit Version Document", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionDocument.vue"),
          },
          {
            path: "documents/new",
            name: names.documentNew,
            meta: { title: "Create Permit Version Document", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionDocuments.vue"),
          },
          {
            path: "materialsPermitted",
            name: names.materialsPermitted,
            meta: { title: "Permit Version Material Permitted" },
            component: () => import("@/views/permitVersion/PermitVersionMaterialsPermitted.vue"),
          },
          {
            path: "materialsPermitted/edit/:import(import)?",
            name: names.materialsPermittedEdit,
            meta: { title: "Edit Permit Version Material Permitted", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionMaterialsPermittedEdit.vue"),
          },
          {
            path: "reportSchedules",
            name: names.reportSchedules,
            meta: { title: "Permit Version Report Schedules" },
            component: () => import("@/views/permitVersion/PermitVersionReportSchedules.vue"),
          },
          {
            path: "reportSchedules/new",
            name: names.reportScheduleNew,
            meta: { title: "Create Permit Version Report Schedule", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionReportScheduleEdit.vue"),
          },
          {
            path: "reportSchedules/import",
            name: names.reportScheduleImport,
            meta: { title: "Import Permit Version Report Schedules", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionReportScheduleImport.vue"),
          },
          {
            path: "reportSchedules/:reportScheduleId(\\d+)",
            name: names.reportScheduleEdit,
            meta: { title: "Edit Permit Version Report Schedule", isForm: true },
            component: () => import("@/views/permitVersion/PermitVersionReportScheduleEdit.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Permit Version Audit Activity" },
            component: {
              render: () => <EntityAudits entityName="PermitVersion" />,
            },
          },
        ],
      },
    ],
  },
];
export default permitVersionRoutes;
